/**
 * app.js — Main entry point
 *
 * Import order:
 * 1. Styles  (Webpack extracts these into a separate CSS bundle)
 * 2. Vendors / polyfills
 * 3. Utilities
 * 4. Modules
 * 5. Components
 * 6. Page-level init
 */

// ─── Styles ───────────────────────────────────────────────────────────────────
import '../css/app.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';
import 'aos/dist/aos.css';

// ─── Vendors ──────────────────────────────────────────────────────────────────
import AOS from 'aos';

// ─── Modules ──────────────────────────────────────────────────────────────────
import Navbar from './components/Navbar.js';
import SmSlider from './components/SmSlider.js';
import EpisodesSlider from './components/EpisodesSlider.js';
import ReplaysSlider from './components/ReplayesSlider.js'; 
import PopularSlider from './components/PopularSlider.js';
import PopularTopicsSlider from './components/PopularTopicsSlider.js';
import RecentlyAddedSlider from './components/RecentlyAddedSlider.js';
import EditProfileModal from './components/EditProfileModal.js';
import ModalTrigger from './components/ModalTrigger.js';
import AskAiOffcanvas from './components/AskAiOffcanvas.js';
import AiOffcanvas from './components/AiOffcanvas.js';
import Tabs from './components/Tabs.js';
import Accordion from './components/Accordion.js';
import PasswordToggle from './components/PasswordToggle.js';
import ScrollEffects from './modules/ScrollEffects.js';
import {
  fadeUpStagger,
  fadeIn,
  refreshScrollTrigger,
} from './modules/Animations.js';

import { prefersReducedMotion, qsa } from './utilities/Utilities.js';

// ─── Init ─────────────────────────────────────────────────────────────────────

const init = () => {
  // data-aos="..." animations
  AOS.init({
    duration: 600,
    easing: 'ease-out-cubic',
    once: true,
    offset: 80,
    disable: prefersReducedMotion,
  });

  // Core UI
  new Navbar();
  new SmSlider();
  new EpisodesSlider();
  new ReplaysSlider();
  new PopularSlider();
  new PopularTopicsSlider();
  new RecentlyAddedSlider();
  new EditProfileModal();
  new ModalTrigger();
  new AskAiOffcanvas();
  new AiOffcanvas();
  new Tabs();
  new Accordion();
  new PasswordToggle();
  new ScrollEffects();

  // Scroll animations — only when motion is acceptable
  if (!prefersReducedMotion()) {
    // Fade-up for any element marked [data-gsap="fade-up"]
    fadeUpStagger(qsa('[data-gsap="fade-up"]'));

    // Fade-in for any element marked [data-gsap="fade-in"]
    qsa('[data-gsap="fade-in"]').forEach((el) => fadeIn(el));

    // Refresh ScrollTrigger after all images have loaded
    window.addEventListener('load', refreshScrollTrigger, { once: true });
  }
};

// Run after DOM is parsed
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
